import { useLoadScript } from '@react-google-maps/api';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

import crossWhite from '../../../assets/img/icons/remove_assignee.svg';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import MapsAutocompleteDropdown from '../../../components/MapsAutocompleteDropdown/MapsAutocompleteDropdown';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import { ClearSearchButton } from '../../../components/Search/searchStyles';
import useAutocompleteService from '../../../customHooks/usePlacesAutocompleteService';
import {
  ColoredButton,
  PrimaryButton,
  RadiusButton,
  WhiteButton,
} from '../../../styledComponents/buttons';
import {
  ColoredText,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../../styledComponents/common';
import { checkHtml, checkUrl } from '../../../utils/helper';
import TaskLocationMap from '../TaskLocationMap';
import {
  AddTaskLocationInfoContainer,
  AddTaskLocationSection,
} from './createTaskStyles';

const mapLibraries = ['places'];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const AddLocationModal = ({
  isOpen,
  toggle,
  // autocomplete and geocoding service
  getGeocode,
  addTaskLocationLoading,
  createdTaskLocationInfo,
  addTaskLocation,
  getTaskLocation,
  getOfficesShortList,
  officesShortList,
  publicZoneList,
  onLocationChange,
}) => {
  const { t } = useTranslation();

  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [radiusInput, setRadiusInput] = useState(100);
  const [saveErrors, setSaveErrors] = useState({});
  const radiusOptions = [50, 100, 150, 200, 300];
  const [isCreated, setIsCreated] = useState(false);
  const [addressMarker, setAddressMarker] = useState(null);
  const [selectedPublicZone, setSelectedPublicZone] = useState('');
  const [publicZoneOptions, setPublicZoneOptions] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [officeOptions, setOfficeOptions] = useState([]);

  // map states
  const [map, setMap] = useState(null);

  const [userCurrentLocation, setUserCurrentLocation] = useState(null);

  const { isLoaded, loadError } = useLoadScript({
    id: 'job-site-details-map-script-loader',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });

  const {
    geocoderService,
    getPlacePredictions,
    placePredictions,
    setPlacePredictions,
    isPredictionsLoading,
    refreshSessionToken,
  } = useAutocompleteService({ map, debounceTimeout: 500 });

  useEffect(() => {
    fetch('https://hutils.loxal.net/whois')
      .then(resp => resp.json())
      .then(res => {
        if (res && res.ip) {
          setUserCurrentLocation({
            lat: res.latitude,
            lng: res.longitude,
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    let list = [
      {
        label: t('common_location_for_all_offices'),
        value: null,
      },
    ];
    setSelectedOffice(list[0]);
    if (officesShortList && officesShortList.length > 0) {
      officesShortList.forEach(office => {
        list.push({
          label: office.name,
          value: office.id,
        });
      });
    }
    setOfficeOptions(list);
  }, [officesShortList]);

  useEffect(() => {
    if (map && userCurrentLocation) {
      map.panTo(userCurrentLocation);
    }
  }, [map, userCurrentLocation]);

  useEffect(() => {
    if (
      createdTaskLocationInfo &&
      createdTaskLocationInfo.id &&
      isOpen &&
      isCreated
    ) {
      handleToggle();
      // getTaskLocation();
      onLocationChange &&
        onLocationChange({
          label: createdTaskLocationInfo.name,
          value: createdTaskLocationInfo.id,
          ...createdTaskLocationInfo,
        });
    }
  }, [createdTaskLocationInfo]);

  useEffect(() => {
    if (publicZoneList && publicZoneList.length > 0) {
      let newArr = [];
      publicZoneList.forEach(zone => {
        newArr.push({
          label: zone.name,
          value: zone.id,
        });
      });
      setPublicZoneOptions(newArr);
    }
  }, [publicZoneList]);

  useEffect(() => {
    if (selectedPublicZone.value) {
      getOfficesShortList({
        public_zone_id: selectedPublicZone.value,
      });
    } else getOfficesShortList();
  }, [selectedPublicZone]);

  const onPublicZoneChange = e => {
    setSelectedPublicZone(e);
  };

  const handleGetGeocode = place => {
    if (geocoderService && place && place.place_id && map) {
      setLocationAddress(place.description);
      geocoderService
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          handleSetAddressMarker(results[0].geometry.location);
        })
        .catch(e => {
          console.log(e);
        });
      setPlacePredictions([]);
    }
    refreshSessionToken();
  };

  const handleReverseGeocode = coord => {
    if (geocoderService && coord) {
      setPlacePredictions([]);
      geocoderService
        .geocode({ location: coord })
        .then(results => {
          if (results.results && results.results.length) {
            setLocationAddress(results.results[0].formatted_address);
          } else {
            setLocationAddress(`${coord.lat()}, ${coord.lng()}`);
          }
        })
        .catch(e => {
          console.log(e);
        });
    }
  };

  const handleSetAddressFromMap = location => {
    if (location && location.latLng) {
      handleSetAddressMarker(location.latLng);
      handleReverseGeocode(location.latLng);
    }
  };
  const handleSetAddressMarker = location => {
    if (location) {
      setAddressMarker(location);

      handleMapPanAndZoomToSingleLocation(location);
    }
  };
  const handleMapPanAndZoomToSingleLocation = location => {
    if (map) {
      map.panTo(location);
      map.setZoom(17);
    }
  };

  const onLocationNameChange = e => {
    const { value } = e.target;
    setLocationName(value);
    if (saveErrors && saveErrors['locationName']) {
      delete saveErrors['locationName'];
    }
  };

  const onLocationAddressChange = e => {
    const { value } = e.target;
    getPlacePredictions({ input: value });
    setLocationAddress(value);
    if (saveErrors && saveErrors['locationAddress']) {
      delete saveErrors['locationAddress'];
    }
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;

    if (checkUrl(locationName)) {
      formIsValid = false;
      saveErrors['locationName'] = 'task location name can not contain url';
    } else if (checkHtml(locationName)) {
      formIsValid = false;
      saveErrors['locationName'] = 'Invalid job site name';
    }
    if (checkUrl(locationAddress)) {
      formIsValid = false;
      saveErrors['locationAddress'] = 'Address can not contain url';
    } else if (checkHtml(locationAddress)) {
      formIsValid = false;
      saveErrors['locationAddress'] = 'Invalid address input';
    }
    if (!radiusInput) {
      formIsValid = false;
      saveErrors['radius'] = 'Radius is required';
    } else if (radiusInput % 1 !== 0) {
      formIsValid = false;
      saveErrors['radius'] = 'Fraction is not allowed for radius';
    } else if (radiusInput < 50) {
      formIsValid = false;
      saveErrors['radius'] = 'Minimum value for radius is 50';
    }
    if (!selectedPublicZone) {
      formIsValid = false;
      saveErrors['public_zone'] = 'Public Zone cannot be empty';
    }
    if (!selectedOffice) {
      formIsValid = false;
      saveErrors['zone'] = 'Office cannot be empty';
    }
    setSaveErrors(saveErrors);
    return formIsValid;
  };

  const handleAddTaskLocation = () => {
    if (checkError()) {
      const payload = {
        name: locationName,
        address: locationAddress,
        radius: radiusInput,
        public_zone_id: selectedPublicZone.value,
      };

      if (selectedOffice && selectedOffice.value) {
        payload.office = selectedOffice.value;
      }

      if (addressMarker && addressMarker.lat) {
        payload.latitude =
          typeof addressMarker.lat === 'function'
            ? addressMarker.lat()
            : addressMarker.lat;
        payload.longitude =
          typeof addressMarker.lng === 'function'
            ? addressMarker.lng()
            : addressMarker.lng;
      }
      // console.log(payload);
      addTaskLocation(payload);
      setIsCreated(true);
    }
  };

  const clearState = () => {
    setLocationName('');
    setLocationAddress('');
    setRadiusInput(100);
    setSaveErrors({});
    setAddressMarker(null);
    setMap(null);
    setUserCurrentLocation(null);
    setIsCreated(false);
  };

  const setDefaultLocation = () => {
    setLocationAddress('Mina, Makkah Saudi Arabia');
    handleSetAddressMarker({
      lat: parseFloat(21.4146051),
      lng: parseFloat(39.894564),
    });
    setUserCurrentLocation({
      lat: parseFloat(21.4146051),
      lng: parseFloat(39.894564),
    });
  };
  useEffect(() => {
    setDefaultLocation();
  }, [isOpen]);
  const handleToggle = () => {
    clearState();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: `2vh`, maxWidth: `950px` }}
    >
      <ModalBody style={{ padding: `0` }}>
        <AddTaskLocationSection>
          <AddTaskLocationInfoContainer>
            <CommonText name title fontSize="16px" margin="auto 0 10px 0">
              {t('add_task_location')}
            </CommonText>

            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t('location_name')} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t('type_location_name')}
                value={locationName}
                onChange={e => onLocationNameChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationName`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors['locationName'] &&
                saveErrors['locationName'] !== undefined && (
                  <FormAlert>{saveErrors['locationName']}</FormAlert>
                )}
            </InputWithLabelSection>
            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t('public_zone')} <RedStar>*</RedStar>
              </InputLabel>
              <Select
                isSearchable
                value={selectedPublicZone}
                options={publicZoneOptions}
                placeholder={t('select_zone')}
                isDisabled={publicZoneList && publicZoneList.length === 1}
                onChange={e => {
                  onPublicZoneChange(e);
                }}
                styles={CreateProjectDropdown()}
                filterOption={filterOption}
              />
              {saveErrors &&
                saveErrors['public_zone'] &&
                saveErrors['public_zone'] !== undefined && (
                  <FormAlert margin="0 0 0 5px">
                    {saveErrors['public_zone']}
                  </FormAlert>
                )}
            </InputWithLabelSection>
            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t('office')} <RedStar>*</RedStar>
              </InputLabel>
              <Select
                isSearchable
                value={selectedOffice}
                options={officeOptions}
                placeholder={t('select_zone')}
                // isDisabled={officesShortList && officesShortList.length === 1}
                onChange={e => {
                  setSelectedOffice(e);
                }}
                styles={CreateProjectDropdown()}
                filterOption={filterOption}
              />
              {saveErrors &&
                saveErrors['zone'] &&
                saveErrors['zone'] !== undefined && (
                  <FormAlert margin="0 0 0 5px">{saveErrors['zone']}</FormAlert>
                )}
            </InputWithLabelSection>
            <InputWithLabelSection
              style={{ position: 'relative' }}
              margin="20px 0 0"
            >
              <InputLabel>
                {t('location_address')} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t('type_location_address')}
                value={locationAddress}
                onChange={e => onLocationAddressChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationAddress`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors['locationAddress'] &&
                saveErrors['locationAddress'] !== undefined && (
                  <FormAlert>{saveErrors['locationAddress']}</FormAlert>
                )}
              {!isPredictionsLoading &&
              placePredictions &&
              placePredictions.length &&
              locationAddress ? (
                <MapsAutocompleteDropdown
                  dropdownOptions={placePredictions}
                  handleSelect={handleGetGeocode}
                />
              ) : null}
            </InputWithLabelSection>

            <InputWithLabelSection margin="20px 0 0">
              <CommonGrid>
                <InputLabel>
                  {t('location_radius')} <RedStar>*</RedStar>
                </InputLabel>
                {/* <ColoredText
                  fontSize="12px"
                  type="warning"
                  hover
                  semibold
                  style={{ cursor: `pointer` }}
                  onClick={() => setIsCustomRadius(true)}
                >
                  Custom Radius
                </ColoredText> */}
              </CommonGrid>
              <CommonFlex justifyContent="start" wrap="wrap" gap="12px">
                {radiusOptions.map((option, i) => {
                  return (
                    <RadiusButton
                      key={option}
                      active={radiusInput === option}
                      onClick={() => {
                        if (radiusInput !== option) {
                          setRadiusInput(option);
                          if (saveErrors && saveErrors['radius']) {
                            delete saveErrors['radius'];
                          }
                        }
                      }}
                    >
                      {option} m
                    </RadiusButton>
                  );
                })}
              </CommonFlex>
            </InputWithLabelSection>
            <CommonGrid
              justifyContent="unset"
              padding="10px 0 0 0"
              margin="30px 0 0"
            >
              <WhiteButton onClick={() => handleToggle()}>
                {t('cancel')}
              </WhiteButton>
              <PrimaryButton
                disabled={!locationName || !locationAddress}
                onClick={() => handleAddTaskLocation()}
              >
                {addTaskLocationLoading ? (
                  <ButtonTextLoader
                    loadingText={t('add_location')}
                    fontSize="13px"
                  />
                ) : (
                  t('add_location')
                )}
              </PrimaryButton>
            </CommonGrid>
          </AddTaskLocationInfoContainer>
          <TaskLocationMap
            setMap={setMap}
            singleAddressMarker={addressMarker}
            setAddressFromMap={handleSetAddressFromMap}
            radius={radiusInput}
          />
        </AddTaskLocationSection>
      </ModalBody>
    </Modal>
  );
};

export default AddLocationModal;
